import { jsx as _jsx } from "react/jsx-runtime";
import { FormatPattern } from "@enums/format";
import { useQuery } from "@tanstack/react-query";
import { startOfDay, isSameMinute } from "date-fns";
import { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { formatDate } from "@utils/DateUtils";
import { PaxPredictionDirection } from "@components/Filters/PaxPredictionDirectionFilter/PaxPredictionDirectionFilter";
import NoDataAvailable from "@components/NoDataAvailable/NoDataAvailable";
import Spinner from "@components/Spinner/Spinner";
import { AvinorColor, breakpoints } from "@constants";
import useFlightMovementsChartData from "@features/FlightMovements/hooks/useFlightMovementsChartData";
import { getPaxPredictionGraphData } from "@features/Pax/services";
import { useMediaQuery } from "@hooks";
import { useDatePickerContext } from "@contexts/DatePickerContext";
import { useIataContext } from "@contexts/IataContext";
import { useUtcContext } from "@contexts/UtcContext";
export const FlightsPaxCombinedChart = () => {
    var _a, _b;
    const { iata } = useIataContext();
    const { t } = useTranslation();
    const { utc } = useUtcContext();
    const is4kScreen = useMediaQuery(`(min-width: ${breakpoints.width4k})`);
    const { from: date, to: dateTo } = useDatePickerContext().picker;
    const dateFormatted = useMemo(() => formatDate(false, date, FormatPattern.yyyy_MM_dd), [date]);
    const { data: paxPredictionGraphDataArrival, isLoading: isLoadingPaxArrival } = useQuery({
        queryFn: () => getPaxPredictionGraphData({ iata, date: dateFormatted, direction: PaxPredictionDirection.Arrival }),
        queryKey: ["getPaxPredictionGraphData", { iata, dateFormatted, direction: PaxPredictionDirection.Arrival }],
    });
    const { data: paxPredictionGraphDataDeparture, isLoading: isLoadingPaxDeparture } = useQuery({
        queryFn: () => getPaxPredictionGraphData({ iata, date: dateFormatted, direction: PaxPredictionDirection.Departure }),
        queryKey: ["getPaxPredictionGraphData", { iata, dateFormatted, direction: PaxPredictionDirection.Departure }],
    });
    const { flightMovements, isLoading: flightsLoading } = useFlightMovementsChartData({
        date: { from: date, to: dateTo },
    });
    if (isLoadingPaxArrival || isLoadingPaxDeparture || flightsLoading)
        return _jsx(Spinner, {});
    if (!paxPredictionGraphDataArrival ||
        !((_a = paxPredictionGraphDataArrival === null || paxPredictionGraphDataArrival === void 0 ? void 0 : paxPredictionGraphDataArrival.intervals) === null || _a === void 0 ? void 0 : _a.some((interval) => interval.total > 0)) ||
        !paxPredictionGraphDataDeparture ||
        !((_b = paxPredictionGraphDataDeparture === null || paxPredictionGraphDataDeparture === void 0 ? void 0 : paxPredictionGraphDataDeparture.intervals) === null || _b === void 0 ? void 0 : _b.some((interval) => interval.total > 0)) ||
        !flightMovements ||
        !flightMovements.length)
        return (_jsx("div", { style: { display: "flex", flexDirection: "column", gap: "var(--avinor-spacing-4)" }, children: _jsx(NoDataAvailable, {}) }));
    // Filter the intervals, so it shows 24 hours of data (utc or not utc)
    const filteredIntervalsArrival = paxPredictionGraphDataArrival.intervals.filter((interval) => {
        const intervalDate = utc ? new Date(interval.from).getUTCDate() : new Date(interval.from).getDate();
        const planDateNumber = date.getDate();
        return intervalDate === planDateNumber;
    });
    const filteredIntervalsDeparture = paxPredictionGraphDataDeparture.intervals.filter((interval) => {
        const intervalDate = utc ? new Date(interval.from).getUTCDate() : new Date(interval.from).getDate();
        const planDateNumber = date.getDate();
        return intervalDate === planDateNumber;
    });
    const filteredIntervals = filteredIntervalsArrival
        .filter((interval) => {
        const date = new Date(interval.from);
        return date.getHours() >= 5;
    })
        .map((interval) => {
        const intervalDeparture = filteredIntervalsDeparture.find((intervalDep) => new Date(intervalDep.from).getTime() === new Date(interval.from).getTime());
        if (intervalDeparture) {
            return {
                from: interval.from,
                total: interval.nonSchengen +
                    intervalDeparture.nonSchengen +
                    interval.schengen +
                    intervalDeparture.schengen +
                    interval.domestic +
                    intervalDeparture.domestic,
            };
        }
        console.error("Couldn't find interval: ", interval);
        return interval;
    });
    //Our pax data is an array of 96 intervals. (15 min intervals for the whole day)
    //Our flight data is an array of 24 intervals (each hour of the day).
    //We need to normalize the flight data to match the pax data. Both need to be 96.
    //We do this by adding a 0 for the 15 min intervals where we have no flight data.
    const flightMovementsNormalized15min = filteredIntervals.map((interval) => {
        var _a, _b, _c, _d;
        const flightMovementsInterval = flightMovements.find((fm) => isSameMinute(new Date(fm.from), new Date(interval.from)));
        return {
            from: interval.from,
            totalFlights: (_a = flightMovementsInterval === null || flightMovementsInterval === void 0 ? void 0 : flightMovementsInterval.totalFlights) !== null && _a !== void 0 ? _a : 0,
            international: (_b = flightMovementsInterval === null || flightMovementsInterval === void 0 ? void 0 : flightMovementsInterval.international) !== null && _b !== void 0 ? _b : 0,
            domestic: (_c = flightMovementsInterval === null || flightMovementsInterval === void 0 ? void 0 : flightMovementsInterval.domestic) !== null && _c !== void 0 ? _c : 0,
            schengen: (_d = flightMovementsInterval === null || flightMovementsInterval === void 0 ? void 0 : flightMovementsInterval.schengen) !== null && _d !== void 0 ? _d : 0,
        };
    });
    const series = [
        {
            name: "Non-Schengen Flights",
            type: "bar",
            data: flightMovementsNormalized15min.map((interval) => interval.international),
            color: AvinorColor.ChartMintGreen300,
            group: "flights",
        },
        {
            name: "Schengen Flights",
            type: "bar",
            data: flightMovementsNormalized15min.map((interval) => interval.schengen),
            color: AvinorColor.ChartVioletLight600,
            group: "flights",
        },
        {
            name: "Domestic Flights",
            type: "bar",
            data: flightMovementsNormalized15min.map((interval) => interval.domestic),
            color: AvinorColor.ChartVioletLight400,
            group: "flights",
        },
        {
            name: "Total Pax",
            type: "area",
            data: filteredIntervals.map((interval) => interval.total),
            color: "#C370AE",
            group: "pax",
            zIndex: 1,
            borderColor: "#B7549E",
            borderWidth: 1,
            opacity: 0.2,
            stacked: true,
        },
    ];
    const xaxisCategories = flightMovementsNormalized15min.map((interval) => utc ? new Date(interval.from).toISOString() : interval.from);
    const scaleFactor = is4kScreen ? 2 : 1;
    const maxFlights = Math.max(...flightMovementsNormalized15min.map((interval) => (interval.international || 0) + (interval.schengen || 0) + (interval.domestic || 0) || 10));
    const maxPax = Math.max(...Object.values(filteredIntervals).map((interval) => Math.max(interval.total)));
    const yAxisFlights = { min: 0, max: Math.ceil((maxFlights * 2.5) / 15) * 15 };
    const yAxisPax = { min: 0, max: Math.ceil((maxPax * 1.1) / 300) * 300 };
    const options = {
        colors: series.map((s) => s.color),
        chart: {
            fontFamily: "Roboto, sans-serif",
            type: "area",
            stacked: true,
            height: 384,
            toolbar: {
                show: false,
            },
            animations: {
                enabled: true,
                easing: "easeinout",
                speed: 600,
                animateGradually: {
                    enabled: false,
                    delay: 0,
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 600,
                },
            },
        },
        grid: {
            show: false,
            xaxis: {
                lines: {
                    show: false,
                },
            },
        },
        tooltip: {
            enabled: false,
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            width: series.map((s) => s.borderWidth || 0),
            curve: "smooth",
            colors: series.map((s) => s.borderColor || ""),
        },
        fill: {
            type: "solid",
            opacity: series.map((s) => { var _a; return (_a = s.opacity) !== null && _a !== void 0 ? _a : 1; }),
        },
        xaxis: {
            categories: xaxisCategories,
            type: "datetime",
            labels: {
                datetimeUTC: utc,
                offsetY: is4kScreen ? 2 : 0,
                style: {
                    fontSize: `0.75rem`,
                    colors: "var(--ds-color-neutral-border-strong)",
                },
            },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "200%",
            },
        },
        yaxis: [
            {
                title: {
                    text: t("common.flights"),
                    style: {
                        color: "var(--ds-color-neutral-border-strong)",
                    },
                },
                labels: {
                    formatter: function (value) {
                        return value.toString();
                    },
                    style: {
                        fontSize: `0.75rem`,
                        colors: "var(--ds-color-neutral-border-strong)",
                    },
                },
                seriesName: ["Non-Schengen Flights", "Schengen Flights", "Domestic Flights"],
                min: yAxisFlights.min,
                max: yAxisFlights.max,
                tickAmount: 3,
            },
            {
                title: {
                    text: t("common.pax"),
                    style: {
                        color: "var(--ds-color-neutral-border-strong)",
                    },
                },
                labels: {
                    formatter: function (value) {
                        return value.toString();
                    },
                    style: {
                        fontSize: `0.75rem`,
                        colors: "var(--ds-color-neutral-border-strong)",
                    },
                },
                seriesName: ["Non-Schengen Pax", "Schengen Pax", "Domestic Pax"],
                opposite: true,
                min: yAxisPax.min,
                max: yAxisPax.max,
                tickAmount: 3,
            },
        ],
        legend: {
            labels: {
                colors: "var(--ds-color-neutral-border-strong)",
            },
        },
        annotations: {
            xaxis: [
                {
                    x: startOfDay(new Date()).getTime(),
                    x2: new Date().getTime(),
                    fillColor: "rgba(51, 62, 78)",
                    opacity: 0.5,
                },
                {
                    x: new Date().getTime(),
                    strokeDashArray: 10 * scaleFactor,
                    label: {
                        text: t ? t("common.now") : "Now",
                        position: "top",
                        offsetY: -15 * scaleFactor,
                        orientation: "horizontal",
                        borderWidth: 0,
                        style: {
                            fontSize: `${12 * scaleFactor}px`,
                            color: "var(--ds-color-neutral-border-strong)",
                            background: "none",
                        },
                    },
                },
            ],
        },
    };
    return (_jsx("div", { children: _jsx(ReactApexChart, { options: options, series: series, type: "area", height: is4kScreen ? 720 : 360 }) }));
};
export default FlightsPaxCombinedChart;
